
import { ReviewCard } from "@/components/ReviewCard";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import type { Review } from "@/types/review";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useState, useEffect } from "react";

interface ProfileReviewsProps {
  reviews: Review[];
  userDetails: {
    username: string;
    avatar_url: string | null;
    link_one: string | null;
    link_two: string | null;
    verification_status?: 'none' | 'pending' | 'verified' | 'rejected' | 'verified_creative';
    is_creative?: boolean;
  };
}

export function ProfileReviews({ reviews, userDetails }: ProfileReviewsProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  // Create review objects with consistent profile data
  const reviewsWithProfiles = reviews.map(review => ({
    ...review,
    profiles: {
      username: userDetails.username,
      avatar_url: userDetails.avatar_url,
      link_one: userDetails.link_one,
      link_two: userDetails.link_two,
      verification_status: userDetails.verification_status,
      is_creative: userDetails.is_creative
    }
  }));

  // Sort by net votes (upvotes - downvotes)
  const sortedReviews = [...reviewsWithProfiles].sort((a, b) => {
    const aUpvotes = a.upvotes || 0;
    const aDownvotes = a.downvotes || 0;
    const bUpvotes = b.upvotes || 0;
    const bDownvotes = b.downvotes || 0;
    
    // Calculate net votes
    const aNetVotes = aUpvotes - aDownvotes;
    const bNetVotes = bUpvotes - bDownvotes;
    
    return bNetVotes - aNetVotes; // Highest net votes first
  });

  return (
    <div>
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="w-full"
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-semibold`}>
              {t('profile_reviews')}
              <span className="ml-2 text-muted-foreground">
                ({reviews.length})
              </span>
            </h2>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="hover:bg-transparent">
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>

        <CollapsibleContent>
          <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
            {reviews.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <h2 className="text-xl font-semibold">{t('profile_no_reviews')}</h2>
                <p className="text-muted-foreground mt-2">{t('profile_share_experience')}</p>
              </div>
            ) : (
              sortedReviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                />
              ))
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
